import moment from 'moment-timezone';
import _ from 'lodash';
import { getEventFieldPath } from '../utils/common';
import { toFilter } from './filterConverter';
import { getGlobalFilter, getProjectZoneId } from '../reducers/analyticsApp';
import { revenueQueryTypes } from '../constants/revenue';
import { getTypeField } from '../utils/event/createQueries';
import { getAggregations, timeUnits, toEventResult, toRange } from './eventConverter';
import { FULL_DATE_FORMAT } from '../constants/date';

const division = (a, b, queryType) =>
  queryType === revenueQueryTypes.ARPU ? _.round(a / b, 2) : _.round((a / b) * 100, 2);

const getDashboardRevenueAggregations = (queryType, amountField, split) => {
  switch (queryType) {
    case revenueQueryTypes.REVENUE:
      return [
        {
          type: 'TOTAL',
          field: amountField,
        },
      ];

    case revenueQueryTypes.TRANSACTIONS:
      return [
        {
          type: 'COUNT',
          field: null,
        },
      ];

    case revenueQueryTypes.ARPPU:
      return [
        {
          type: split ? 'AVERAGE_TOTAL_SPLIT' : 'AVERAGE_TOTAL',
          field: amountField,
        },
      ];

    default:
      throw new Error('Unrecognized query type');
  }
};

const mapDashboardRevenueResult = (response, query, dashboardRevenueInfo, unit) => {
  const { getType } = dashboardRevenueInfo;
  const events = [toEventResult(response, query, '', getType)];
  const options = toRange(events[0].info);

  return {
    events,
    options,
    unit,
  };
};

const createQuery = (state, eventName, queryType, unit) => {
  const groupBy = unit.compose.revenue.groupColumn;
  const split = unit.compose.revenue.splitColumn;

  const groups = split ? [groupBy, split] : [groupBy];
  const amountField = getEventFieldPath(unit.compose.revenue.amountField);
  const paymentFilter = toFilter(unit.compose.revenue.paymentFilter);

  const query = {
    eventName,
    aggregations: [
      {
        type: queryType === revenueQueryTypes.ARPU ? 'TOTAL' : 'USERS',
        field: queryType === revenueQueryTypes.ARPU ? amountField : null,
      },
    ],
    groupBy: groups,
    globalFilter: toFilter(getGlobalFilter(state)),
    timezone: getProjectZoneId(state),
  };

  if (eventName === 'Payment') {
    query.filter = paymentFilter;
  }

  return query;
};

const mapDashboardComposeRevenueResult = (
  dashboardComposeRevenueInfo,
  unit,
  payersPaymentQuery,
  payersUsersQuery,
  payment,
  dau
) => {
  const { getType } = dashboardComposeRevenueInfo;
  const { queryType } = unit.compose.revenue;
  if (!payment || !dau) {
    return undefined;
  }
  const [groupBy, splitBy] = payersPaymentQuery.groupBy;

  const paymentAggregation = getAggregations(payersPaymentQuery.aggregations)[0];
  const userAggregation = getAggregations(payersUsersQuery.aggregations)[0];

  const rows = payment.rows
    .map((paymentRow) => {
      const userRow = dau.rows.find(
        (userRowItem) =>
          userRowItem[groupBy] === paymentRow[groupBy] &&
          userRowItem[splitBy] === paymentRow[splitBy]
      );

      if (userRow === undefined) {
        return userRow;
      }

      return splitBy === undefined
        ? {
            [groupBy]: userRow[groupBy],
            [queryType]: division(
              paymentRow[paymentAggregation],
              userRow[userAggregation],
              queryType
            ),
          }
        : {
            [groupBy]: userRow[groupBy],
            [queryType]: division(
              paymentRow[paymentAggregation],
              userRow[userAggregation],
              queryType
            ),
            [splitBy]: userRow[splitBy],
          };
    })
    .filter((row) => row !== undefined);

  return {
    events: [
      {
        type: 'eventsResult',
        info: {
          typeGroupBy: getType(groupBy).type,
          timezone: payersUsersQuery.timezone,
          eventName: queryType,
          query: payersUsersQuery,
          uniqueChar: '',
          aggregation: [queryType],
          groupBy,
          splitBy,
          timeUnit: timeUnits[groupBy],
        },
        result: {
          columns: [groupBy, splitBy, queryType].filter((c) => c !== undefined),
          rows: rows.filter((row) => Object.keys(row).length > 0),
        },
      },
    ],
    options: {
      from: moment
        .tz(payersUsersQuery.globalFilter.from, FULL_DATE_FORMAT, payersUsersQuery.timezone)
        .startOf(timeUnits[groupBy])
        .valueOf(),
      to: moment
        .tz(payersUsersQuery.globalFilter.to, FULL_DATE_FORMAT, payersUsersQuery.timezone)
        .startOf(timeUnits[groupBy])
        .valueOf(),
    },
    unit,
  };
};

export const buildDashboardRevenueQuery = (state, unit) => {
  const groupBy = unit.compose.revenue.groupColumn;
  const split = unit.compose.revenue.splitColumn;
  const { queryType } = unit.compose.revenue;
  const groups = split ? [groupBy, split] : [groupBy];
  const amountField = getEventFieldPath(unit.compose.revenue.amountField);

  return {
    eventName: 'Payment',
    aggregations: getDashboardRevenueAggregations(queryType, amountField, split),
    groupBy: groups,
    filter: toFilter(unit.compose.revenue.paymentFilter),
    globalFilter: toFilter(getGlobalFilter(state)),
    timezone: getProjectZoneId(state),
  };
};

export const buildDashboardComposeRevenueQueries = (state, unit) => {
  const { queryType } = unit.compose.revenue;
  return [
    createQuery(state, 'Payment', queryType, unit),
    createQuery(state, 'DAU', undefined, unit),
  ];
};

export const getDashboardRevenueInfo = (state, unit) => {
  const { queryType } = unit.compose.revenue;
  const getType = (field) => getTypeField(field, state);

  return { queryType, getType };
};

export const fromDashboardRevenueResponse = (response, query, dashboardRevenueInfo, unit) => {
  const { events, options } = mapDashboardRevenueResult(
    response,
    query,
    dashboardRevenueInfo,
    unit
  );
  const { queryType } = dashboardRevenueInfo;
  return { events, options, queryType, unit };
};

export const getDashboardComposeRevenueInfo = (state, unit) => {
  const { queryType } = unit.compose.revenue;
  const getType = (field) => getTypeField(field, state);

  return { queryType, getType };
};

export const fromDashboardComposeRevenueResponse = (
  response,
  queries,
  dashboardComposeRevenueInfo,
  unit
) => {
  const [payment, dau] = response;
  const [paymentQuery, dauQuery] = queries;
  const { queryType } = dashboardComposeRevenueInfo;
  const { events, options } = mapDashboardComposeRevenueResult(
    dashboardComposeRevenueInfo,
    unit,
    paymentQuery,
    dauQuery,
    payment,
    dau
  );
  return { events, options, queryType, unit };
};
